import {
    rangeSlice,
    getMatchingPatterns,
    setChromatogramPaths,
    histogramPath,
} from "./utils";

import {
    alignBpPosition,
    isInRange,
    linearDomain, setThreshold,
} from "../editor/utils";

import {
    colors,
    ChromatogramDictionary,
    MatchingPatternColors, thresholdLevels,
} from '../mixins/dictionaries';

import Brush from "./linearBrush";

import {viewMixins, brushMixins} from "./alignmentMixins";

import {select, selectAll} from "d3-selection";
import {scaleLinear} from "d3-scale"
import {axisBottom} from "d3-axis"
import {drag} from "d3-drag"
import {zoom as scroll} from "d3-zoom";
import {line} from "d3-shape";

const d3 = {select, selectAll, scaleLinear, axisBottom, drag, scroll, line}

export default class Read {
    constructor(selector, slots, boxContainer, regularSizeFont, offsetStart, referenceLength, alignmentLength, containerWidth, mediator) {
        this.slots = slots;
        this.overlaps = [offsetStart, (2 * offsetStart) + referenceLength];

        this.settings = {
            offsetStart: offsetStart
        }
        this.selector = selector;
        this.$mainBoxContainer = boxContainer;
        this.$containerWidth = containerWidth;
        this.regularSizeFont = regularSizeFont;
        this.referenceLength = referenceLength;
        this.sequenceLength = alignmentLength;
        this.mediator = mediator
        this.ends = [];
        this.slots.forEach(() => {
            if (this.settings.offsetStart) {
                this.ends.push(this.settings.offsetStart)
            }
            this.ends.push(this.settings.offsetStart + this.referenceLength)
        })

        Object.assign(this, viewMixins, brushMixins);

        this.EventsOn();

        this.initContainer();
        this.brush = new Brush();
    }

    initData(data) {
        this.settings = {
            ...this.settings,
            id: data.code,
            start: data.start - data.start_trim,
            end: data.end + data.end_trim,
            start_trim: data.start_trim,
            end_trim: data.end_trim,
            length: data.end - data.start,
        }

        this.readMatchingPatterns = getMatchingPatterns(this.settings.start + data.start_trim, data.start_trim, this.settings.end - data.end_trim, data.end_trim, data.mismatches, data.deletions, data.insertions)

        this.maxHeightChromatograms = 0;
        this.maxQualityValue = 0;

        let [chromatogramPaths, maxHeight] = setChromatogramPaths(data.chromatograms);

        this.sequence = data.sequence;
        this.readLength = this.sequence.length

        this.chromatograms = chromatogramPaths;
        this.qualityValues = data.quality_values;

        this.maxHeightChromatograms = Math.max(this.maxHeightChromatograms, maxHeight)
        this.maxQualityValue = Math.max(this.maxQualityValue, ...data.quality_values)

        this.linearDomainY = linearDomain([0, this.maxHeightChromatograms], -100);

        this.initPosition = 0;
        this.rangeBp = {
            start: 0,
            end: 0
        };

        this.offsetScroll = {
            "current": this.initPosition,
            "step": 0,
        }

        this.searchResult = {
            data: [],
            textLength: 0
        };

        this.setEditorParameters();

        this.middleArea = alignBpPosition(this.offsetScroll.current, Math.round(this.maxBasePairsInAreaView / 2));
        this.getEdges(this.maxBasePairsInAreaView, this.sequenceLength);

        this.readContainer = this.mainBox.append('g')
            .attr("transform", "translate(0,0)");

        this.createBrush();
    }

    initContainer() {
        this.zoom = 12;
        this.fontHeight = 12;
        this.userAgent = window.navigator.userAgent;

        this.$readsBoxContainer = d3.select(this.selector);

        this.mainBox = this.$readsBoxContainer
            .append("svg")
            .style("height", "100%")
            .style("width", "100%")

        this.addDefs();
        this.addEventsContainer();
    }

    addEventsContainer() {
        this.mainBox
            .on("contextmenu", (e) => {
                e.preventDefault();
                e.stopPropagation();
            })
            .call(d3.drag()
                .on("start drag end", e => this.eventOnCreateBrush(e))
            )
    }

    setData(data) {
        this.initData(data)
        this.render();
    }

    render() {
        this.drawSequence();
        this.drawChromatograms();

        this.drawMatchingPatterns();
        this.drawQualityValues();
        this.drawBorders([this.settings.offsetStart, this.settings.offsetStart + this.referenceLength]);
    }

    drawBasePairs(container, basePairs, startEdge) {
        let translationX = startEdge + (this.fontBoxWidth / 2);
        const sequence = Array.from(basePairs)

        sequence.forEach(bp => {
            if (this.threshold >= thresholdLevels.overFontWidth) {
                this.drawBasePair(container, bp, translationX)
            } else {
                this.drawBasePairPlaceHolder(container, bp, translationX - (this.fontBoxWidth / 2))
            }

            translationX += this.fontBoxWidth;
        })
    }

    drawSequence() {
        const basePairContainerPosition = 88
        let count = 0;
        let translationX = (this.fontBoxWidth / 2);

        const groups = this.mainBox.selectAll("g.basePairGroup").nodes();

        this.slots.forEach(r => {
            if (isInRange(r.start, r.end, this.edges.left, this.edges.right)) {
                const start = 0;
                const end = r.end - r.start;
                const left = this.edges.left - r.start;
                const right = this.edges.right - r.start;

                const [startBasePairsSlice, basePairsLengthSlice] = rangeSlice(
                    start,
                    end,
                    this.sequenceLength,
                    left,
                    right
                )

                if (groups[0]) {
                    groups[0]
                        .setAttribute("transform", "translate(" + (start * this.fontBoxWidth) + "," + basePairContainerPosition + ")")

                    const basePairNodes = d3.select(groups[0]).selectAll("text.basePairSequence").nodes();
                    const basePairs = this.sequence.slice(startBasePairsSlice, startBasePairsSlice + basePairsLengthSlice)

                    if (basePairNodes.length) {
                        translationX += (this.offsetScroll.current - start) * this.fontBoxWidth

                        if (start > left) {
                            translationX += (start - left) * this.fontBoxWidth
                        }

                        for (let x = 0; x < basePairs.length; x++) {
                            if (basePairNodes[count]) {
                                basePairNodes[count].removeAttribute("filter")
                                basePairNodes[count].classList.remove("text-decoration-line-through");

                                basePairNodes[count].setAttribute("x", translationX)
                                basePairNodes[count].style.fill = colors[basePairs[x]] || "#000";
                                basePairNodes[count].textContent = basePairs[x];
                            } else {
                                this.drawBasePair(d3.select(groups[0]), basePairs[x], translationX)
                            }
                            translationX += this.fontBoxWidth;
                            count++
                        }

                        if (basePairNodes.length >= count) {
                            for (let index = count; index < basePairNodes.length; index++) {
                                basePairNodes[index].remove()
                            }
                        }
                    } else {
                        const container = this.readContainer
                            .append("g")
                            .attr("class", "basePairGroup")
                            .attr("transform", "translate(" + (r.start * this.fontBoxWidth) + "," + basePairContainerPosition + ")")

                        const sliceBasePairs = this.sequence.slice(startBasePairsSlice, startBasePairsSlice + basePairsLengthSlice)

                        this.drawBasePairs(container, sliceBasePairs, startBasePairsSlice * this.fontBoxWidth)
                    }
                } else {
                    const container = this.readContainer
                        .append("g")
                        .attr("class", "basePairGroup")
                        .attr("transform", "translate(" + (r.start * this.fontBoxWidth) + "," + basePairContainerPosition + ")")

                    const sliceBasePairs = this.sequence.slice(startBasePairsSlice, startBasePairsSlice + basePairsLengthSlice)

                    this.drawBasePairs(container, sliceBasePairs, startBasePairsSlice * this.fontBoxWidth)
                }
            }
        })


    }

    drawBasePair(container, basePair, translationX) {
        container
            .append("text")
            .attr("class", "basePairSequence fs-6 fw-lighter")
            .attr("x", translationX)
            .attr("dy", 10)
            .style("text-anchor", "middle")
            .style('fill', colors[basePair])
            .text(basePair);
    }

    drawBasePairPlaceHolder(container, basePair, translationX) {
        if (this.threshold > thresholdLevels.full) {
            container
                .append("rect")
                .attr("class", "basePairSequence")
                .attr("x", translationX)
                .attr("y", 0)
                .attr("width", this.fontBoxWidth)
                .attr("height", 20)
                .style('fill', colors[basePair])
                .style('opacity', .6)
        }
    }

    drawChromatograms() {
        this.readContainer.selectAll(".basePairChromatograms").remove();
        if (this.zoom !== 20) {
            this.slots.forEach(r => {
                if (isInRange(r.start, r.end, this.edges.left, this.edges.right)) {

                    Object.entries(this.chromatograms).forEach(([key, chromatogram]) => {
                        chromatogram[0].forEach(c => {
                            if (isInRange(c.start + r.start, c.start + c.length + r.start, this.edges.left, this.edges.right)) {
                                const [startPath, lengthPath] = rangeSlice(
                                    c.start + r.start,
                                    c.start + c.length + r.start,
                                    this.sequenceLength,
                                    this.edges.left,
                                    this.edges.right
                                );

                                const slicePath = c.path.slice(startPath, startPath + lengthPath);

                                this.drawLineChart(slicePath, key, c.start + r.start)
                            }
                        })
                    })
                }
            })
        }
    }

    drawLineChart(chromatogramsPath, key, startPath) {
        const chromatogramContainerYPosition = this.offsetScroll.current ? 100 : 85;
        const path = chromatogramsPath.flat().map(p => [p[0] * this.fontBoxWidth, this.linearDomainY(p[1])]);
        const line = d3.line();

        const container = this.readContainer
            .append("g")
            .attr("x", (startPath * this.fontBoxWidth))
            .attr("class", "basePairChromatograms")
            .attr("transform", "translate(" + (startPath * this.fontBoxWidth) + "," + chromatogramContainerYPosition + ")")

        container.append("path")
            .attr("fill", "none")
            .attr("stroke", colors[ChromatogramDictionary[key]])
            .attr("stroke-width", .3)
            .attr("opacity", "7")
            .attr("d", line(path));
    }

    drawMatchingPatterns() {
        const basePairBoxes = this.readContainer.selectAll(".basePairSequence").nodes();
        this.slots.forEach(r => {
            if (isInRange(r.start, r.end, this.edges.left, this.edges.right)) {

                this.readMatchingPatterns.forEach(e => {
                    const offsetRead = this.settings.start;
                    if (isInRange(e.start - offsetRead + r.start, e.end - offsetRead + r.start, this.edges.left, this.edges.right)) {
                        if (this.edges.left > r.start) {
                            let start = e.start - offsetRead - this.edges.left + r.start;
                            for (let i = e.start - offsetRead - this.edges.left + r.start; i < e.start - offsetRead + r.start - this.edges.left + e.length; i++) {
                                const box = basePairBoxes[start];
                                if (box) {
                                    box.removeAttribute("filter");
                                    box.classList.remove("text-decoration-line-through");

                                    if (e.type === "trim") {
                                        box.classList.add("text-decoration-line-through");
                                    } else {
                                        box.style.fill = "white"
                                        box.setAttribute("filter", "url(#textBackground" + e.type + ")");
                                    }
                                }
                                start++
                            }
                        } else {
                            let start = e.start - offsetRead;

                            for (let i = e.start - offsetRead + r.start; i < e.start - offsetRead + r.start + e.length; i++) {
                                const box = basePairBoxes[start];
                                if (box) {
                                    if (e.type === "trim") {
                                        box.removeAttribute("filter")
                                        box.classList.add("text-decoration-line-through");
                                    } else {
                                        box.classList.remove("text-decoration-line-through")
                                        box.style.fill = "white"
                                        box.setAttribute("filter", "url(#textBackground" + e.type + ")");
                                    }
                                }
                                start++
                            }
                        }
                    }
                })
            }
        })
    }

    drawQualityValues() {
        let qualityValuesContainerYPosition = this.zoom === 20 ? 99 : 90;
        this.readContainer.selectAll(".basePairQualityValueGroup").remove();
        this.readContainer.selectAll(".basePairQualityValue").remove();
        const opacityQuality =  this.zoom === 20 ? .5: .1
        const line = d3.line();

        this.slots.forEach(r => {
            if (isInRange(r.start, r.end, this.edges.left, this.edges.right)) {
                const [startPath, lengthPath] = rangeSlice(
                    r.start,
                    r.start + this.qualityValues.length,
                    this.qualityValues.length,
                    this.edges.left,
                    this.edges.right
                )

                const slicePath = this.qualityValues.slice(startPath, startPath + lengthPath)
                const path = histogramPath(slicePath, (90 / this.maxQualityValue), 0.05, this.fontBoxWidth);


                const container = this.readContainer
                    .append("g")
                    .attr("class", "basePairQualityValueGroup")
                    .attr("x", (startPath * this.fontBoxWidth))
                    .attr("transform", "translate(" + ((r.start + startPath) * this.fontBoxWidth) + "," + qualityValuesContainerYPosition + ")")

                container.append("path")
                    .attr("class", "basePairQualityValue")
                    .attr("stroke-width", .5)
                    .attr("opacity", opacityQuality)
                    .attr("d", line(path));
            }
        })
    }

    drawResultMatchSearches() {
        const matchSearchY = 87
        const height = 16
        this.mainBox.selectAll(".matchSearch").remove();

        this.searchResult.data.forEach(r => {
            if (isInRange(r.pos, r.pos + r.textLength, this.edges.left, this.edges.right)) {
                this.mainBox
                    .append("rect")
                    .attr("class", "matchSearch")
                    .attr("y", matchSearchY)
                    .attr("x", ((r.pos - this.offsetScroll.current) * this.fontBoxWidth))
                    .attr("width", (this.searchResult.textLength * this.fontBoxWidth))
                    .attr("height", height)
                    .style("fill", "yellow")
                    .style("opacity", .4)
                    .style("stroke", "#000")
                    .style("stroke-width", 1)
            }
        })
    }

    /**
     *
     * @param data = statusScroll
     */
    slide(data) {
        this.readContainer
            .attr("transform", "translate(" + -(this.offsetScroll.current * this.fontBoxWidth) + ",0)")

        this.drawChromatograms();

        if (this.referenceLength !== this.sequenceLength) {
            this.drawBorders([this.settings.offsetStart, this.settings.offsetStart + this.referenceLength]);
        }

        if (data === "end") {
            const chromatogramGroups = this.mainBox.selectAll("g.basePairChromatograms").nodes();

            chromatogramGroups.forEach(c => {
                c.setAttribute("transform", "translate( " + c.getAttribute("x") + ",85) ")
            })

            this.drawQualityValues()
            this.drawSequence();
            this.drawMatchingPatterns();

            if (this.zoom !== 20) {
                this.middleArea = alignBpPosition(this.offsetScroll.current, Math.round(this.maxBasePairsInAreaView / 2));
            }

        } else {
            this.mainBox.selectAll(".matchSearch").remove();
            this.mainBox.selectAll("g.basePairQualityValueGroup").remove()
            this.mainBox.selectAll("g.basePairGroup").remove()
        }
    }

    setFontWidth(data) {
        this.zoom = data;

        this.setEditorParameters();
        this.getEdges(this.maxBasePairsInAreaView, this.sequenceLength);

        this.mainBox.selectAll(".basePairSequence").remove();

        this.slide("end")
    }


    setScroll(data) {
        const minLeftOffset = 0
        const maxRightOffset = this.sequenceLength - this.maxBasePairsInAreaView;
        this.offsetScroll.current -= data;
        this.offsetScroll.step -= data;

        if (this.offsetScroll.current < minLeftOffset || this.offsetScroll.current > maxRightOffset) {
            if (this.offsetScroll.current < minLeftOffset) {
                this.offsetScroll.current = minLeftOffset
            } else {
                this.offsetScroll.current = maxRightOffset
            }
        } else {
            this.edges.left -= data;
            this.edges.right -= data;
        }
    }

    setOffsetScroll(data) {
        this.offsetScroll.current = data.current;

        const newBrushSize = {
            x: (this.rangeBp.start - this.offsetScroll.current) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
        }
        this.brush.setParameters(newBrushSize)

        this.middleArea = this.offsetScroll.current + Math.round(this.maxBasePairsInAreaView / 2);
        this.getEdges(this.maxBasePairsInAreaView, this.sequenceLength);
    }

    addDefs() {
        const filterDef = this.mainBox.append("defs");
        const matchingPatternColorDefs = ["del", "mis", "ins"];

        matchingPatternColorDefs.forEach(mp => {
            const filter = filterDef.append("filter")
                .attr("id", "textBackground" + mp)
                .attr("x", 0)
                .attr("y", 0)
                .attr("width", 1)
                .attr("height", 1);
            filter
                .append("feFlood")
                .attr("flood-color", MatchingPatternColors[mp])
                .attr("flood-opacity", .7)
            const filterMerge = filter.append("feMerge");
            filterMerge.append("feMergeNode")
                .attr("in", "txtBackground" + mp);
            filterMerge.append("feMergeNode")
                .attr("in", "SourceGraphic");
        })
    }

    EventsTrigger(action, data) {
        if (this.mediator) {
            this.mediator.trigger(action, data, this)
        }
    }

    EventsOn() {
        if (this.mediator) {
            this.mediator.on("setFontWidth", "setFontWidth", this);
            this.mediator.on("hideBrush", "hideBrush", this);
            this.mediator.on("showBrush", "showBrush", this);

            this.mediator.on("setScroll", "setScroll", this);
            this.mediator.on("slide", "slide", this);

            this.mediator.on("setOffsetScroll", "setOffsetScroll", this);

            this.mediator.on("updateBrush", "updateBrush", this);

            this.mediator.on("search", "search", this);
            this.mediator.on("resetSearch", "resetSearch", this);

            this.mediator.on("moveOnSearchResult", "moveOnSearchResult", this);

            this.mediator.on("selectRange", "selectRange", this);
            this.mediator.on("copyBasePairsSelected", "copyBasePairsSelected", this);
        }
    }
}